/**
 * Resolves generated art (see scripts/src/port-assets.ts) to Vite-hashed URLs.
 * Files: cards/<roleId>.<w>.{webp,avif}, frames/<team|back|neutral>.<w>.{webp,avif}, tokens/<id>.<w>.{webp,avif}
 */
import type { RoleId, TeamId, TokenImageId } from '@onuw/roles-data'

const files = import.meta.glob<string>('../assets/{cards,frames,tokens,misc}/*.{webp,avif}', {
  eager: true,
  import: 'default',
  query: '?url',
})

const lookup = (path: string): string | undefined => files[`../assets/${path}`]

/** Vector card frames drawn for the oval card (scripts/src/make-card-frames.ts). */
const frames = import.meta.glob<string>('../assets/card-frames/*.svg', {
  eager: true,
  import: 'default',
  query: '?url',
})

export type ImageSources = { avif: string; webp: string; fallback: string; width: number }

const sources = (dir: string, name: string, widths: readonly number[]): ImageSources[] =>
  widths.flatMap((w) => {
    const webp = lookup(`${dir}/${name}.${w}.webp`)
    const avif = lookup(`${dir}/${name}.${w}.avif`)
    return webp ? [{ webp, avif: avif ?? webp, fallback: webp, width: w }] : []
  })

const srcSet = (list: ImageSources[], key: 'webp' | 'avif') =>
  list.map((s) => `${s[key]} ${s.width}w`).join(', ')

export interface ResponsiveImage {
  /** `<source type="image/avif" srcSet>` value. */
  avifSet: string
  /** `<source type="image/webp" srcSet>` value. */
  webpSet: string
  /** `<img src>` fallback (largest webp). */
  src: string
  available: boolean
}

const responsive = (dir: string, name: string, widths: readonly number[]): ResponsiveImage => {
  const list = sources(dir, name, widths)
  const largest = list.at(-1)
  return {
    avifSet: srcSet(list, 'avif'),
    webpSet: srcSet(list, 'webp'),
    src: largest?.fallback ?? '',
    available: list.length > 0,
  }
}

export const CARD_WIDTHS = [320, 482, 720] as const
export const FRAME_WIDTHS = [482, 720] as const
export const TOKEN_WIDTHS = [160, 320] as const

export const cardArt = (role: RoleId): ResponsiveImage => responsive('cards', role, CARD_WIDTHS)
export const frameImage = (team: TeamId | 'back' | 'neutral'): ResponsiveImage =>
  responsive('frames', team, FRAME_WIDTHS)
/** Face frame for a team: `full` (nameplate + ability plaque) or `mini` (compact face for small cards). */
export const cardFrame = (team: TeamId, variant: 'full' | 'mini' = 'full'): string =>
  frames[`../assets/card-frames/${team}${variant === 'mini' ? '.mini' : ''}.svg`] ?? ''
/** Rim drawn over the engraved card-back artwork. */
export const cardBackFrame = (): string => frames['../assets/card-frames/back.svg'] ?? ''
export const tokenImage = (token: TokenImageId): ResponsiveImage => responsive('tokens', token, TOKEN_WIDTHS)
export const miscImage = (name: 'moon' | 'parchment-texture' | 'x-mark', widths: readonly number[]) =>
  responsive('misc', name, widths)
